import { format, parse } from 'date-fns'
import { autoCapitalize } from '@/utils/autoCapitalize'
import { formatCPF } from '@/utils/formatCPF/formatCPF'
import { FormRegisterPatient } from '@/modules/Users/pages/Patient/components/RegisterPatient/RegisterPatient.types'
import { RegisterPatientData } from '../../PatientServices/PatientServices.types'

class RegisterPatientMapper {
  toPersistence(
    domainData: FormRegisterPatient & Partial<RegisterPatientData>,
  ): RegisterPatientData {
    return {
      id: domainData?.id,
      idConvenio: domainData?.idConvenio,
      idConvenioPlano: domainData?.idConvenioPlano,
      flagMultiplosCartoesConvenio: domainData?.flagMultiplosCartoesConvenio,
      idPacienteInformacaoComplementar:
        domainData?.idPacienteInformacaoComplementar,
      flagAcessouSuaSaude: domainData?.flagAcessouSuaSaude,
      idContatoEmergencia: domainData?.idContatoEmergencia,
      convenio: domainData?.convenio,
      convenioPlano: domainData?.convenioPlano,
      idPaisNascimento: domainData?.idPaisNascimento,
      foto: domainData?.foto,
      carteiraConvenio: domainData?.healthInsurance,
      clinica: {
        id: domainData?.clinic?.id,
        nome: domainData?.clinic?.name,
      },
      titularidade: domainData?.patientType?.value,
      titular: {
        id: domainData?.patientPlanHolder?.id,
        nome: domainData?.patientPlanHolder?.name,
        cpf: domainData?.patientPlanHolder?.cpf,
      },
      grauParentesco: domainData?.relationshipPlanHolder?.value,
      nomeRepresentanteLegal: domainData?.nameLegalRepresentative,
      cpfRepresentanteLegal: domainData?.cpfLegalRepresentative,
      grauParentescoRepresentanteLegal:
        domainData?.relationshipLegalRepresentative?.value,
      representanteLegal:
        domainData?.planHolderIsLegalRepresentative?.value &&
        domainData?.planHolderIsLegalRepresentative?.value !== 'NAO',
      nome: domainData?.name,
      cpf: domainData?.cpf,
      sexo: domainData?.gender?.value,
      email: domainData?.email,
      telefoneCelular: domainData?.phone,
      dataNascimento:
        domainData?.dateOfBirth &&
        format(new Date(domainData?.dateOfBirth), 'dd/MM/yyyy'),
      nomeSocial: domainData?.socialName,
      infoAdicionais: domainData?.additionalInformation,
      plano: domainData?.plan,
      matricula: domainData?.matriculation,
      empresa: domainData?.company,
      departamento: domainData?.department,
      cargo: domainData?.role,
      nomeCompletoEmergencia: domainData?.emergencyContactName,
      relacaoEmergencia: domainData?.emergencyRelationship?.value,
      telefoneEmergencia: domainData?.emergencyPhone,
      cep: domainData?.cep,
      cidade: domainData?.city,
      uf: domainData?.state?.label,
      bairro: domainData?.neighborhood,
      numeroLogradouro: domainData?.addressNumber,
      endereco: domainData?.street,
      complemento: domainData?.complement,
      flagMaeDesconhecida:
        domainData?.unknownMother?.value &&
        domainData?.unknownMother?.value !== 'NAO',
      nomeMae: domainData?.motherName,
      idUfNascimento: domainData?.stateOfBirth?.value,
      municipioNascimento: domainData?.birthPlace,
      identidade: domainData?.RG,
      idUfIdentidade: domainData?.stateIssueRG?.value,
      orgaoEmissorIdentidade: domainData?.organIssueRG,
      dataEmissaoIdentidade:
        domainData?.dateIssueRG &&
        format(new Date(domainData?.dateIssueRG), 'dd/MM/yyyy'),
      numeroNis: domainData?.NIS,
      idUfNis: domainData?.stateIssueNIS?.value,
      dataEmissaoNis:
        domainData?.dateIssueNIS &&
        format(new Date(domainData?.dateIssueNIS), 'dd/MM/yyyy'),
      orgaoEmissorNis: domainData?.organIssueNIS,
      cns: domainData?.cns,
      idGenero: domainData?.genderSupplementary?.value,
      idRacaCor: domainData?.skin?.value,
      religiao: domainData?.religion?.value,
      outrasReligioes: domainData?.otherReligion,
      idEscolaridade: domainData?.educationLevel?.value,
      idAreaMoradia: domainData?.livingArea?.value,
      flagSituacaoMoradia:
        domainData?.homelessPatient?.value &&
        domainData?.homelessPatient?.value !== 'NAO',
      flagVulnerabilidadeSocial:
        domainData?.socialVunerability?.value &&
        domainData?.socialVunerability?.value !== 'NAO',
      idCbo: domainData?.occupation?.id,
      localTrabalho: domainData?.workplace,
      idNacionalidade: domainData?.nationality?.value,
      idPaisNascimentoEstrangeiro: domainData?.countryOfOrigin?.value,
      dataNaturalizacao:
        domainData?.dateOfNaturalization &&
        format(new Date(domainData?.dateOfNaturalization), 'dd/MM/yyyy'),
      passaporte: domainData?.passport,
      idPaisEmissorPassaporte: domainData?.issuingCountry?.value,
      dataEmissaoPassaporte:
        domainData?.dateOfIssuePassport &&
        format(new Date(domainData?.dateOfIssuePassport), 'dd/MM/yyyy'),
      dataValidadePassaporte:
        domainData?.expirationDatePassport &&
        format(new Date(domainData?.expirationDatePassport), 'dd/MM/yyyy'),
      obsLivres: domainData?.observations,
      clinicPlan: domainData?.clinicPlan || '',
      idClienteExterno: domainData?.externalClientId,
    }
  }

  toDomain(persistenceData: RegisterPatientData) {
    return {
      id: persistenceData?.id,
      idConvenio: persistenceData?.idConvenio,
      idConvenioPlano: persistenceData?.idConvenioPlano,
      flagMultiplosCartoesConvenio:
        persistenceData?.flagMultiplosCartoesConvenio,
      idPacienteInformacaoComplementar:
        persistenceData?.idPacienteInformacaoComplementar,
      flagAcessouSuaSaude: persistenceData?.flagAcessouSuaSaude,
      idContatoEmergencia: persistenceData?.idContatoEmergencia,
      convenio: persistenceData?.convenio,
      convenioPlano: persistenceData?.convenioPlano,
      idPaisNascimento: persistenceData?.idPaisNascimento,
      foto: persistenceData?.foto,
      photo: persistenceData?.foto,
      healthInsurance: persistenceData?.carteiraConvenio,
      clinic: {
        id: persistenceData?.clinica?.id,
        name: persistenceData?.clinica?.nome,
        flags: {
          hasDisabledAgreement:
            persistenceData?.clinica?.flagPossuiConvenioDesabilitado,
          hasMultipleAgreement:
            persistenceData?.clinica?.flagHabilitarMultiplosCartoesConvenio,
        },
        product: persistenceData?.produto,
      },
      patientType: persistenceData?.titularidade,
      patientPlanHolder: {
        id: persistenceData?.titular?.id,
        name: persistenceData?.titular?.nome,
        cpf: persistenceData?.titular?.cpf,
      },
      relationshipPlanHolder: persistenceData?.grauParentesco,
      nameLegalRepresentative: persistenceData?.nomeRepresentanteLegal,
      cpfLegalRepresentative:
        persistenceData?.cpfRepresentanteLegal &&
        formatCPF(persistenceData?.cpfRepresentanteLegal),
      relationshipLegalRepresentative:
        persistenceData?.grauParentescoRepresentanteLegal,
      planHolderIsLegalRepresentative: persistenceData?.representanteLegal,
      name: autoCapitalize(persistenceData?.nome),
      cpf: formatCPF(persistenceData?.cpf),
      gender: persistenceData?.sexo,
      email: persistenceData?.email,
      phone: persistenceData?.telefoneCelular,
      dateOfBirth:
        persistenceData?.dataNascimento &&
        parse(persistenceData?.dataNascimento, 'dd/MM/yyyy', new Date()),
      socialName: persistenceData?.nomeSocial,
      additionalInformation: persistenceData?.infoAdicionais,
      plan: persistenceData?.plano,
      matriculation: persistenceData?.matricula,
      company: persistenceData?.empresa,
      department: persistenceData?.departamento,
      role: persistenceData?.cargo,
      healthInsurances: persistenceData?.healthInsurances,
      emergencyContactName: autoCapitalize(
        persistenceData?.nomeCompletoEmergencia || '',
      ),
      emergencyRelationship: persistenceData?.relacaoEmergencia,
      emergencyPhone: persistenceData?.telefoneEmergencia,
      bankCard: persistenceData?.cartoes,
      cep: persistenceData?.cep,
      city: autoCapitalize(persistenceData?.cidade || ''),
      state: autoCapitalize(persistenceData?.uf || ''),
      neighborhood: autoCapitalize(persistenceData?.bairro || ''),
      addressNumber: persistenceData?.numeroLogradouro,
      street: autoCapitalize(persistenceData?.endereco || ''),
      complement: persistenceData?.complemento,
      unknownMother: persistenceData?.flagMaeDesconhecida,
      motherName: persistenceData?.nomeMae,
      stateOfBirth: persistenceData?.idUfNascimento,
      birthPlace: persistenceData?.municipioNascimento,
      RG: persistenceData?.identidade,
      stateIssueRG: persistenceData?.idUfIdentidade,
      organIssueRG: persistenceData?.orgaoEmissorIdentidade,
      dateIssueRG:
        persistenceData?.dataEmissaoIdentidade &&
        parse(persistenceData?.dataEmissaoIdentidade, 'dd/MM/yyyy', new Date()),
      NIS: persistenceData?.numeroNis,
      stateIssueNIS: persistenceData?.idUfNis,
      dateIssueNIS:
        persistenceData?.dataEmissaoNis &&
        parse(persistenceData?.dataEmissaoNis, 'dd/MM/yyyy', new Date()),
      organIssueNIS: persistenceData?.orgaoEmissorNis,
      cns: persistenceData?.cns,
      genderSupplementary: persistenceData?.idGenero,
      skin: persistenceData?.idRacaCor,
      religion: persistenceData?.religiao,
      otherReligion: persistenceData?.outrasReligioes,
      educationLevel: persistenceData?.idEscolaridade,
      livingArea: persistenceData?.idAreaMoradia,
      homelessPatient: persistenceData?.flagSituacaoMoradia,
      socialVunerability: persistenceData?.flagVulnerabilidadeSocial,
      occupation: persistenceData?.idCbo,
      workplace: persistenceData?.localTrabalho,
      nationality: persistenceData?.idNacionalidade,
      countryOfOrigin: persistenceData?.idPaisNascimentoEstrangeiro,
      dateOfNaturalization:
        persistenceData?.dataNaturalizacao &&
        parse(persistenceData?.dataNaturalizacao, 'dd/MM/yyyy', new Date()),
      passport: persistenceData?.passaporte,
      issuingCountry: persistenceData?.idPaisEmissorPassaporte,
      dateOfIssuePassport:
        persistenceData?.dataEmissaoPassaporte &&
        parse(persistenceData?.dataEmissaoPassaporte, 'dd/MM/yyyy', new Date()),
      expirationDatePassport:
        persistenceData?.dataValidadePassaporte &&
        parse(
          persistenceData?.dataValidadePassaporte,
          'dd/MM/yyyy',
          new Date(),
        ),
      observations: persistenceData?.obsLivres || '',
      clinicPlan: persistenceData?.clinicPlan || '',
      externalClientId: persistenceData?.idClienteExterno,
    }
  }
}

export default new RegisterPatientMapper()
